import React, { useMemo } from 'react';
import { ColumnDef, SortingState } from '@tanstack/react-table';
import { TanTable } from '../../../components/TanTable';
import {
  TurnoverRow,
  bandClass,
  rankOf,
  NEUTRAL_TEXT,
  fmtInt,
  turnoverPct,
} from './logic';

/**
 * Driver turnover for one period: one row per dispatcher, with the headcount it
 * opened and closed on and the hires and terminations in between.
 *
 * A flat table rather than the dispatcher × date matrix the other metrics use —
 * the four figures are different measures, not the same measure over time, so
 * they belong in their own columns. The period is chosen by the tabs above.
 */

/** Which of the four counts a cell is, i.e. which unit list it opens. */
export type TurnoverCell = 'start_units' | 'hire_units' | 'term_units' | 'finish_units';

interface Props {
  rows: TurnoverRow[];   // already narrowed to the selected period
  /** The span the period measured, e.g. '1/1/26 – 3/31/26'. */
  periodRange: string;
  /** 'Q3 2026' — for the empty state, where a bare date range reads oddly. */
  periodLabel: string;
  /** Opens the drivers behind one number. */
  onOpenDrivers: (row: TurnoverRow, cell: TurnoverCell, columnLabel: string) => void;
}

const TurnoverTable: React.FC<Props> = ({ rows, periodRange, periodLabel, onOpenDrivers }) => {
  const { data, columns, defaultSorting } = useMemo(() => {
    const data = [...rows];

    // Rank shading over the dispatchers that have a rate, so the colouring is
    // not thrown by the Unassigned line (which has no meaningful rate).
    const rates = data
      .filter((r) => !r.unassigned)
      .map((r) => turnoverPct(r))
      .filter((v): v is number => v != null);

    const sum = (key: keyof TurnoverRow) =>
      data.reduce((s, r) => s + ((r[key] as number) || 0), 0);

    const numCol = (
      id: keyof TurnoverRow & string,
      header: string,
      units: TurnoverCell,
    ): ColumnDef<TurnoverRow> => ({
      id,
      accessorFn: (row) => row[id] as number,
      header,
      enableSorting: true,
      enableColumnFilter: false,
      size: 80,
      meta: {
        align: 'center',
        exportValue: (row: TurnoverRow) => row[id] as number,
      },
      cell: ({ row }) => {
        const n = row.original[id] as number;
        // A zero has no drivers behind it, so it stays plain text rather than
        // offering a click that opens an empty list.
        if (!n) return <span className="text-muted">0</span>;
        return (
          <button
            type="button"
            onClick={() => onOpenDrivers(row.original, units, header)}
            className="underline cursor-pointer bg-transparent border-0 p-0"
            style={{ color: 'inherit', font: 'inherit' }}
            title={`Show the ${n} driver${n === 1 ? '' : 's'} behind this number`}
          >
            {fmtInt(n)}
          </button>
        );
      },
      footer: () => <span className="font-semibold">{fmtInt(sum(id))}</span>,
    });

    const columns: ColumnDef<TurnoverRow>[] = [
      {
        id: 'dispatcher',
        accessorKey: 'dispatcher',
        header: 'Dispatcher',
        enableSorting: true,
        enableColumnFilter: true,
        filterFn: 'arrIncludes',
        size: 190,
        meta: { exportValue: (row: TurnoverRow) => row.dispatcher },
        cell: ({ row }) => (
          <span className={row.original.unassigned ? 'italic text-muted pr-1' : undefined}>
            {row.original.dispatcher}
          </span>
        ),
        footer: () => <span className="font-semibold">All</span>,
      },
      {
        id: 'region',
        accessorKey: 'region',
        header: 'Region',
        enableSorting: true,
        enableColumnFilter: true,
        filterFn: 'arrIncludes',
        size: 130,
        meta: { exportValue: (row: TurnoverRow) => row.region },
      },
      numCol('start_count', 'Start', 'start_units'),
      numCol('hires', 'Hired', 'hire_units'),
      numCol('terms', 'Left', 'term_units'),
      numCol('finish_count', 'End', 'finish_units'),
      {
        id: 'turnover',
        accessorFn: (row) => turnoverPct(row) ?? undefined,
        header: 'Turnover',
        enableSorting: true,
        enableColumnFilter: false,
        size: 100,
        sortUndefined: 'last',
        meta: {
          align: 'center',
          cellBgFn: (_v: unknown, row: TurnoverRow) => {
            if (row.unassigned) return NEUTRAL_TEXT;
            const band = rankOf(rates, turnoverPct(row), true);
            return band == null ? NEUTRAL_TEXT : bandClass(band === 'top' ? 'good' : 'bad');
          },
          exportValue: (row: TurnoverRow) => turnoverPct(row) ?? '',
        },
        cell: ({ row }) => {
          const r = row.original;
          const pct = turnoverPct(r);
          if (pct == null) return <span className="text-muted">-</span>;
          // A period that ends at zero drivers divides by a headcount collapsing
          // toward zero, so the rate stops being comparable. The counts beside
          // it are still right, which is what the tooltip says.
          const collapsed = r.finish_count === 0;
          return (
            <span
              title={
                collapsed
                  ? `${r.dispatcher} ended the period with no drivers, so this rate is measured `
                    + 'against a near-zero headcount; read the counts rather than the percentage.'
                  : `${r.terms} left against an average headcount of `
                    + `${((r.start_count + r.finish_count) / 2).toFixed(1)}`
              }
            >
              {Math.round(pct)}%{collapsed ? '*' : ''}
            </span>
          );
        },
        footer: () => {
          const avg = (sum('start_count') + sum('finish_count')) / 2;
          return (
            <span className="font-semibold">
              {avg > 0 ? `${Math.round((100 * sum('terms')) / avg)}%` : '-'}
            </span>
          );
        },
      },
    ];

    // Best turnover first; the rows without a rate (Unassigned) sink to the
    // bottom via sortUndefined.
    const defaultSorting: SortingState = [{ id: 'turnover', desc: false }];
    return { data, columns, defaultSorting };
  }, [rows, onOpenDrivers]);

  if (data.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center p-6">
        <p className="text-sm text-gray-400 dark:text-gray-500">No driver turnover in {periodLabel}.</p>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col min-h-0">
      <TanTable
        data={data}
        columns={columns}
        loading={false}
        getRowId={(r) => r.dispatcher_key}
        defaultSorting={defaultSorting}
        settingsKey="dispatcher-metrics-turnover"
        enableColumnEditing={false}
        enableColumnPinning={false}
        cardTitle={periodRange}
        searchPlaceholder="Search dispatchers..."
        enableColumnFilters
        enableSearch
        enableExcelExport
        showColumnFooter
        fillContainer
        // One row per dispatcher, so the row count is bounded by the roster
        // (~30). Virtualizing that costs more than it saves.
        disableVirtualization
        defaultPageSize={500}
      />
    </div>
  );
};

export default TurnoverTable;
